import { cn } from "@babybjorn/ui";
import {
	useEffect,
	useRef,
	useState,
	type FC,
	type PropsWithChildren,
} from "react";
import Button from "../Button";
import * as s from "./Scrollbar.module.scss";

type ScrollbarProps = PropsWithChildren<{
	className?: string;
	onScroll?: () => void;
	ariaLabelScroll: string;
	slider?: boolean;
	scrollbarLength?: number;
}>;

const Scrollbar: FC<ScrollbarProps> = ({
	children,
	className,
	onScroll,
	ariaLabelScroll,
	slider = false,
	scrollbarLength,
}) => {
	const listRef = useRef<HTMLUListElement>(null);

	const [shouldScrollLeft, setShouldScrollLeft] = useState(false);
	const [shouldScrollRight, setShouldScrollRight] = useState(false);

	const calcTabsWidth = () => {
		if (!listRef.current) return;

		const listEl = listRef.current;

		const totalTabsWidth = [...listEl.childNodes]
			.map((t) => {
				const tab = t as HTMLLIElement;
				return tab.offsetWidth;
			})
			.reduce((partialSum, tabWidth) => partialSum + tabWidth, 0);

		setShouldScrollRight(
			totalTabsWidth + 45 > (listEl.parentElement?.offsetWidth || 0),
		);

		setShouldScrollLeft(
			totalTabsWidth + 45 > (listEl.parentElement?.offsetWidth || 0),
		);
	};

	useEffect(() => {
		calcTabsWidth();

		window.addEventListener("resize", calcTabsWidth);
		return () => {
			window.removeEventListener("resize", calcTabsWidth);
		};
		// eslint-disable-next-line react-hooks-addons/no-unused-deps
	}, [scrollbarLength]);

	const scrollClickHandler = (direction: "left" | "right") => {
		if (listRef.current) {
			if (direction === "right") {
				listRef.current.scrollBy({
					top: 0,
					left: 150,
					behavior: "smooth",
				});
			} else {
				listRef.current.scrollBy({
					top: 0,
					left: -150,
					behavior: "smooth",
				});
			}
		}
	};

	const thumbWidth = 35;
	const [thumbLeft, setThumbLeft] = useState(0);

	useEffect(() => {
		const updateThumbPosition = () => {
			if (!listRef.current) return;
			const scrollContainer = listRef.current;
			const contentWidth = scrollContainer.scrollWidth;
			const containerWidth = scrollContainer.offsetWidth;

			const maxScrollLeft = contentWidth - containerWidth;
			const ratio = scrollContainer.scrollLeft / maxScrollLeft || 0;

			setThumbLeft(ratio * (containerWidth - thumbWidth));
		};

		const container = listRef.current;
		if (!container) return;
		container.addEventListener("scroll", updateThumbPosition);
		window.addEventListener("resize", updateThumbPosition);

		updateThumbPosition();

		return () => {
			container.removeEventListener("scroll", updateThumbPosition);
			window.removeEventListener("resize", updateThumbPosition);
		};
	}, [thumbWidth]);

	const handleDragStart = (startX: number) => {
		if (!listRef.current) return;
		const startScrollLeft = listRef.current.scrollLeft;

		const handleDragMove = (moveX: number) => {
			const deltaX = moveX - startX;
			const scrollContainer = listRef.current;
			if (!scrollContainer) return;
			const contentWidth = scrollContainer.scrollWidth;
			const containerWidth = scrollContainer.offsetWidth;

			const maxScrollLeft = contentWidth - containerWidth;
			const ratio = maxScrollLeft / (containerWidth - thumbWidth);

			scrollContainer.scrollLeft = startScrollLeft + deltaX * ratio;
		};

		const stopDrag = () => {
			document.removeEventListener("mousemove", onMouseMove);
			document.removeEventListener("mouseup", stopDrag);
			document.removeEventListener("touchmove", onTouchMove);
			document.removeEventListener("touchend", stopDrag);
		};

		const onMouseMove = (e: MouseEvent) => handleDragMove(e.clientX);
		const onTouchMove = (e: TouchEvent) => {
			if (e.touches[0]) {
				handleDragMove(e.touches[0].clientX);
			}
		};
		document.addEventListener("mousemove", onMouseMove);
		document.addEventListener("mouseup", stopDrag);
		document.addEventListener("touchmove", onTouchMove);
		document.addEventListener("touchend", stopDrag);
	};

	const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) =>
		handleDragStart(e.clientX);
	const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
		if (e.touches[0]) {
			handleDragStart(e.touches[0].clientX);
		}
	};
	return (
		<div
			className={cn(s.scrollWrapper, s.scrollWithArrow, {
				[s.scrollOverflow]: shouldScrollLeft || shouldScrollRight,
			})}
		>
			<ul
				ref={listRef}
				className={cn(s.scrollContainer, className, {
					[s.noScroll]: !shouldScrollLeft && !shouldScrollRight,
				})}
				onScroll={onScroll}
			>
				{children}
			</ul>
			<div className={s.customScrollbar}>
				{shouldScrollRight && (
					<span
						className={s.customThumb}
						style={{ width: `${thumbWidth + 50}px`, left: `${thumbLeft}px` }}
						onMouseDown={handleMouseDown}
						onTouchStart={handleTouchStart}
						role="scrollbar"
						aria-controls="scrollContainer"
						aria-label={ariaLabelScroll}
						aria-orientation="horizontal"
						aria-valuemin={0}
						aria-valuemax={100}
						aria-valuenow={thumbLeft}
						tabIndex={0}
					/>
				)}
			</div>
			{!slider && shouldScrollRight && (
				<div className={s.scrollRightButton}>
					<Button
						buttonType="icon"
						icon="arrow-right"
						variant="container"
						aria-label={ariaLabelScroll}
						onClick={() => scrollClickHandler("right")}
					/>
				</div>
			)}
		</div>
	);
};

export default Scrollbar;
